// Homepage delivery-area selector.
//
// - Lists all MP2 delivery areas from the public catalog.
// - Persists the selected area id to localStorage under `mp2.deliveryAreaId`
//   so downstream components (Today's Menu, future cart) can pick it up.
// - Broadcasts changes via a `mp2-delivery-area-changed` window event so
//   other components can react without lifting state.
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";

export const DELIVERY_AREA_STORAGE_KEY = "mp2.deliveryAreaId";
export const DELIVERY_AREA_EVENT = "mp2-delivery-area-changed";

export function readSelectedAreaId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(DELIVERY_AREA_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSelectedAreaId(id: string) {
  try {
    window.localStorage.setItem(DELIVERY_AREA_STORAGE_KEY, id);
    window.dispatchEvent(new CustomEvent(DELIVERY_AREA_EVENT, { detail: { id } }));
  } catch {
    /* noop */
  }
}

export function useSelectedDeliveryAreaId(): string | null {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    setId(readSelectedAreaId());
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>).detail;
      setId(detail?.id ?? readSelectedAreaId());
    };
    window.addEventListener(DELIVERY_AREA_EVENT, onChange);
    return () => window.removeEventListener(DELIVERY_AREA_EVENT, onChange);
  }, []);
  return id;
}

export function DeliveryAreaSelector({ className }: { className?: string }) {
  const { catalog, isMp2Visible } = usePublicMp2Catalog();
  const [selected, setSelected] = useState<string>("");

  useEffect(() => {
    const stored = readSelectedAreaId();
    if (stored) setSelected(stored);
  }, []);

  if (!isMp2Visible || !catalog || catalog.delivery_areas.length === 0) return null;

  const areas = [...catalog.delivery_areas].sort((a, b) =>
    a.neighbourhood.localeCompare(b.neighbourhood),
  );

  return (
    <div className={className}>
      <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-muted-foreground">
        Choose your delivery area
      </label>
      <Select
        value={selected}
        onValueChange={(v) => {
          setSelected(v);
          writeSelectedAreaId(v);
        }}
      >
        <SelectTrigger className="w-full sm:w-72" aria-label="Select delivery area">
          <SelectValue placeholder="Select your neighbourhood…" />
        </SelectTrigger>
        <SelectContent>
          {areas.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {a.neighbourhood}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-2 text-xs text-muted-foreground">
        We'll tailor today's menu and pricing to your neighbourhood.
      </p>
    </div>
  );
}